import { normalizeRole } from './rbac';

/**
 * RBAC role mapping for dashboard routes.
 *
 * This is the SINGLE SOURCE OF TRUTH for which roles can access which
 * dashboards. It's consumed by:
 *   - apps/web/src/app/(dashboard)/dashboards/layout.tsx (RoleGuard wrapper)
 *   - apps/web/src/components/command-palette.tsx (filter Cmd+K results)
 *
 * Keep this file in sync with the actual role checks in any new dashboard
 * routes — adding a route to layout.tsx without adding it here will cause
 * the command palette to show the item to all roles. The tradeoff is one
 * extra import; the alternative is duplicate truth in two files which is
 * exactly what BUG-11 surfaced.
 */

/** Per-route role allowlists for dashboard pages. */
export const DASHBOARD_ROLES: Record<string, string[]> = {
  '/dashboards': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'REGION_COMPLIANCE_OFFICER',
    'REGION_FINANCE_OFFICER',
    'COORDINATOR',
    'TEAM_LEADER',
    'REGISTERED_NURSE',
    'CLINICAL_LEAD',
    'ENROLLED_NURSE',
    'ALLIED_HEALTH',
    'FINANCE_OFFICER',
    'PAYROLL_OFFICER',
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
    'CASE_MANAGER',
  ],
  '/dashboards/executive': ['SYSTEM_ADMIN', 'TENANT_ADMIN'],
  '/dashboards/multi-facility': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'FACILITY_MANAGER',
  ],
  '/dashboards/don-clinical': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'REGISTERED_NURSE',
    'CLINICAL_LEAD',
    'FACILITY_MANAGER',
  ],
  '/dashboards/finance': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'REGION_FINANCE_OFFICER',
    'FINANCE_OFFICER',
    'PAYROLL_OFFICER',
    'FACILITY_MANAGER',
    'COORDINATOR',
    'QUALITY_OFFICER',
  ],
  '/dashboards/huddle': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'COORDINATOR',
    'TEAM_LEADER',
    'REGISTERED_NURSE',
    'CLINICAL_LEAD',
    'ENROLLED_NURSE',
  ],
  '/dashboards/caseload': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'COORDINATOR',
    'REGISTERED_NURSE',
    'CLINICAL_LEAD',
    'CASE_MANAGER',
    'ALLIED_HEALTH',
  ],
  '/dashboards/operations': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'REGION_COMPLIANCE_OFFICER',
    'COORDINATOR',
    'FACILITY_MANAGER',
    'CASE_MANAGER',
  ],
  '/dashboards/risk-correlation': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'REGION_COMPLIANCE_OFFICER',
    'COORDINATOR',
    'REGISTERED_NURSE',
    'CLINICAL_LEAD',
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
  ],
  '/dashboards/iot': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'REGION_COMPLIANCE_OFFICER',
    'COORDINATOR',
    'REGISTERED_NURSE',
    'CLINICAL_LEAD',
  ],
  '/dashboards/home-care-ops': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'REGION_COMPLIANCE_OFFICER',
    'COORDINATOR',
    'CASE_MANAGER',
    'TEAM_LEADER',
  ],
  '/dashboards/facility-ops': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'COORDINATOR',
    'TEAM_LEADER',
    'REGISTERED_NURSE',
    'CLINICAL_LEAD',
    'FACILITY_MANAGER',
  ],
  '/dashboards/compliance': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'REGION_COMPLIANCE_OFFICER',
    'COORDINATOR',
    'TEAM_LEADER',
    'REGISTERED_NURSE',
    'CLINICAL_LEAD',
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
  ],
  '/dashboards/quality': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'REGION_COMPLIANCE_OFFICER',
    'COORDINATOR',
    'REGISTERED_NURSE',
    'CLINICAL_LEAD',
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
  ],
  '/dashboards/workforce': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'COORDINATOR',
    'TEAM_LEADER',
    'FACILITY_MANAGER',
  ],
  '/dashboards/workforce-analytics': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'FACILITY_MANAGER',
    'COORDINATOR',
  ],
  '/dashboards/region-roll-up': ['SYSTEM_ADMIN', 'TENANT_ADMIN', 'REGION_MANAGER'],
  '/compliance/calendar?view=agenda': [
    'SYSTEM_ADMIN',
    'TENANT_ADMIN',
    'REGION_MANAGER',
    'REGION_COMPLIANCE_OFFICER',
    'REGION_FINANCE_OFFICER',
    'COORDINATOR',
    'REGISTERED_NURSE',
    'CLINICAL_LEAD',
    'FACILITY_MANAGER',
    'QUALITY_OFFICER',
    'FINANCE_OFFICER',
    'PAYROLL_OFFICER',
  ],
};

function getMatchedDashboardRoles(pathname: string): string[] | undefined {
  let matchedPath: string | null = null;
  let longestMatch = 0;

  for (const path of Object.keys(DASHBOARD_ROLES)) {
    const isMatch =
      path === '/dashboards'
        ? pathname === path
        : pathname === path || pathname.startsWith(path + '/');
    if (isMatch && path.length > longestMatch) {
      matchedPath = path;
      longestMatch = path.length;
    }
  }

  return matchedPath ? DASHBOARD_ROLES[matchedPath]! : undefined;
}

export function hasDashboardRoleMapping(pathname: string): boolean {
  return Boolean(getMatchedDashboardRoles(pathname));
}

/**
 * Returns true if the given user role is allowed to access the given path.
 * Uses longest-prefix matching against the DASHBOARD_ROLES table. Dashboard
 * routes fail closed unless they have an explicit policy.
 */
export function isRoleAllowedForPath(pathname: string, role: string | null | undefined): boolean {
  const normalizedRole = normalizeRole(role);
  if (!normalizedRole) return false;
  const allowed = getMatchedDashboardRoles(pathname);
  if (!allowed) return false;
  return allowed.map((candidate) => normalizeRole(candidate)).includes(normalizedRole);
}

export function getAllowedDashboardRoles(pathname: string): string[] {
  return getMatchedDashboardRoles(pathname) ?? [];
}
